import React, { useState, useEffect, useCallback } from "react";
import { Slider } from "antd";
import classNames from "classnames";
import debounce from "lodash/debounce";

// interfaces
import { RangeSliderProps } from "./IRangeSlider";

//components
import LabelErrorProvider from "@/components/LabelErrorProvider";
import { InputNumber } from "@/shared/Input";

//assets
import styles from "../../assets/styles/shared/range-slider.module.scss";

const RangeSlider = (props: RangeSliderProps) => {
  const {
    className,
    onChange,
    wrapperClassName,
    label,
    error,
    withInputs,
    min,
    max,
    value,
    formatter,
    parser,
    inputDataTest,
    ...restProps
  } = props;
  const [rangeValue, setRangeValue] = useState<number[]>(value || [min, max]);
  const [minValue, maxValue] = rangeValue;

  const sliderClassNames = classNames(styles["locl-range-slider"], className);
  const isInputsDisabled =
    (!minValue && typeof minValue !== "number") ||
    (!maxValue && typeof maxValue !== "number") ||
    min === max;

  // effects
  useEffect(() => {
    const minValueToSet = !value || value[0] < min ? +min : value[0];
    const maxValueToSet = !value || value[1] > max ? +max : value[1];

    setRangeValue([minValueToSet, maxValueToSet]);
  }, [min, max, JSON.stringify(value)]);

  // methods
  const debouncedOnChange = useCallback(debounce(e => onChange && onChange(e), 1000), []);

  const handleChange = (val: number[] | number, type?: string) => {
    if (!type) {
      setRangeValue(val as number[]);
      debouncedOnChange(val as number[]);

      return;
    }

    if (type === "min") {
      setRangeValue([+val, maxValue]);
      debouncedOnChange([+val, maxValue]);
    } else {
      setRangeValue([minValue, +val]);
      debouncedOnChange([minValue, +val]);
    }
  };

  // render methods
  const renderInputs = () => {
    const defaultInputProps = {
      fullWidth: true,
      disabled: isInputsDisabled,
      className: styles["locl-range-slider__input"],
      "data-testid": inputDataTest ? inputDataTest : "locl-range-slider-input",
      formatter: formatter,
      parser: parser
    };

    return (
      <div className={styles["locl-range-slider__inputs_wrap"]}>
        <InputNumber
          {...defaultInputProps as Record<string, unknown>}
          value={+minValue}
          min={min}
          max={+maxValue}
          onChange={(e) => handleChange(e as number, "min")}
        />
        <InputNumber
          {...defaultInputProps as Record<string, unknown>}
          value={+maxValue}
          min={+minValue}
          max={max}
          onChange={(e) => handleChange(e as number, "max")}
        />
      </div>
    );
  };

  return (
    <LabelErrorProvider className={wrapperClassName} label={label} error={error}>
      <Slider
        range
        className={sliderClassNames}
        disabled={isInputsDisabled}
        defaultValue={[min, max]}
        onChange={(e) => handleChange(e)}
        value={[+minValue, +maxValue]}
        min={min}
        max={max}
        aria-label="locl-range-slider"
        {...restProps}
      />
      {withInputs && renderInputs()}
    </LabelErrorProvider>
  );
};

export default RangeSlider;
